import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import SendIcon from '@mui/icons-material/Send';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { useRealEstate } from '../../contexts/RealEstateContext';
import { C21_COLORS } from '../../constants/realEstate';

const BuyerActivityReport = () => {
  const { getAllBuyers, getBuyerViewings } = useRealEstate();
  const buyers = getAllBuyers();

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBuyer, setExpandedBuyer] = useState(null);
  const [viewMode, setViewMode] = useState(0); // 0 = Flat Table, 1 = Grouped by Buyer
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Get all viewings with buyer info for flat table
  const allViewings = useMemo(() => {
    const viewings = [];
    buyers.forEach((buyer) => {
      const buyerViewings = getBuyerViewings(buyer.id);
      buyerViewings.forEach((viewing) => {
        viewings.push({
          ...viewing,
          buyerName: buyer.name,
          buyerEmail: buyer.email,
          buyerPhone: buyer.phone,
          buyerPreApproved: buyer.preApproved,
        });
      });
    });
    return viewings;
  }, [buyers, getBuyerViewings]);

  // Filter viewings for flat table
  const filteredViewings = useMemo(() => {
    return allViewings.filter((viewing) => {
      const matchesSearch =
        viewing.buyerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        viewing.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
        viewing.comments?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [allViewings, searchTerm]);

  // Filter buyers for grouped view
  const filteredBuyers = useMemo(() => {
    return buyers.filter((buyer) => {
      const matchesSearch =
        buyer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        buyer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        buyer.preferredLocations.some((loc) =>
          loc.toLowerCase().includes(searchTerm.toLowerCase())
        );
      return matchesSearch;
    });
  }, [buyers, searchTerm]);

  const handleAccordionChange = (buyerId) => (event, isExpanded) => {
    setExpandedBuyer(isExpanded ? buyerId : null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatPrice = (price) => {
    return `$${(price / 1000).toFixed(0)}K`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const paginatedViewings = filteredViewings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header with Tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: C21_COLORS.black }}>
            Buyer Activity Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {viewMode === 0 ? 'All property viewings in one view' : 'Viewings grouped by buyer'}
          </Typography>
        </Box>
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => {
            setViewMode(newValue);
            setPage(0);
          }}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              px: 2,
              fontSize: '0.8rem',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: C21_COLORS.gold,
            },
            '& .Mui-selected': {
              color: `${C21_COLORS.darkGold} !important`,
            },
          }}
        >
          <Tab icon={<ViewListIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Flat View" />
          <Tab icon={<AccountBoxIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="By Buyer" />
        </Tabs>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder={viewMode === 0 ? "Search by buyer, property, or feedback..." : "Search buyers by name, email, or location..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {viewMode === 0
          ? `Showing ${filteredViewings.length} viewings`
          : `Showing ${filteredBuyers.length} of ${buyers.length} buyers`}
      </Typography>

      {/* Flat Table View */}
      {viewMode === 0 && (
        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: C21_COLORS.lightGold,
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#faf8f5' }}>
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Buyer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Property</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Commission</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Offer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Feedback</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedViewings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <HomeIcon sx={{ fontSize: 40, color: C21_COLORS.lightGold, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No viewings found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedViewings.map((viewing) => (
                    <TableRow
                      key={viewing.id}
                      sx={{
                        '&:hover': { backgroundColor: '#faf8f5' },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: C21_COLORS.gold,
                              color: C21_COLORS.black,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {viewing.buyerName.split(' ').map((n) => n[0]).join('')}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {viewing.buyerName}
                            </Typography>
                            {viewing.buyerPreApproved ? (
                              <Chip
                                label="Pre-Approved"
                                size="small"
                                color="success"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            ) : (
                              <Chip
                                label="Pending"
                                size="small"
                                color="warning"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                          {viewing.propertyAddress}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {formatDate(viewing.dateSeen)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem', color: C21_COLORS.black }}>
                          ${viewing.price.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${viewing.compensation}%`}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 22,
                            backgroundColor: `${C21_COLORS.gold}20`,
                            color: C21_COLORS.darkGold,
                            borderColor: C21_COLORS.gold,
                            fontWeight: 600,
                          }}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {viewing.offerSubmitted ? (
                          <Chip
                            icon={<SendIcon sx={{ fontSize: 12 }} />}
                            label="Yes"
                            size="small"
                            color="success"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Chip
                            label="No"
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.8rem',
                            maxWidth: 250,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={viewing.comments}
                        >
                          {viewing.comments || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredViewings.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Card>
      )}

      {/* Grouped by Buyer View */}
      {viewMode === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredBuyers.map((buyer) => {
            const viewings = getBuyerViewings(buyer.id);

            return (
              <Accordion
                key={buyer.id}
                expanded={expandedBuyer === buyer.id}
                onChange={handleAccordionChange(buyer.id)}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                    borderColor: C21_COLORS.lightGold,
                  },
                  '&:hover': {
                    borderColor: C21_COLORS.lightGold,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    '& .MuiAccordionSummary-content': {
                      my: 1.5,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                    {/* Avatar */}
                    <Avatar
                      sx={{
                        bgcolor: C21_COLORS.gold,
                        color: C21_COLORS.black,
                        width: 48,
                        height: 48,
                        fontWeight: 600,
                      }}
                    >
                      {buyer.name.split(' ').map((n) => n[0]).join('')}
                    </Avatar>

                    {/* Buyer Info */}
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {buyer.name}
                        </Typography>
                        {buyer.preApproved ? (
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label="Pre-Approved"
                            size="small"
                            color="success"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Chip
                            icon={<WarningIcon sx={{ fontSize: 14 }} />}
                            label="Pending Approval"
                            size="small"
                            color="warning"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {buyer.email} | {buyer.phone}
                      </Typography>
                    </Box>

                    {/* Quick Stats */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AttachMoneyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formatPrice(buyer.priceRange.min)} - {formatPrice(buyer.priceRange.max)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {buyer.bedrooms} Bed
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BathtubIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {buyer.bathrooms} Bath
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HomeIcon sx={{ fontSize: 18, color: C21_COLORS.gold }} />
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem', color: C21_COLORS.darkGold }}>
                          {viewings.length} Properties Viewed
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 3 }}>
                  {/* Buyer Details Card */}
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: '#faf8f5',
                      border: '1px solid',
                      borderColor: C21_COLORS.lightGold,
                      borderRadius: 2,
                      mb: 3,
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                        Buyer Preferences
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Price Range
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatPrice(buyer.priceRange.min)} - {formatPrice(buyer.priceRange.max)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Bedrooms
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {buyer.bedrooms}+
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Bathrooms
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {buyer.bathrooms}+
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Pre-Approval Amount
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {buyer.preApprovalAmount
                              ? `$${buyer.preApprovalAmount.toLocaleString()}`
                              : 'Not pre-approved'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Preferred Locations */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <LocationOnIcon sx={{ fontSize: 14 }} />
                          Preferred Locations
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {buyer.preferredLocations.map((location) => (
                            <Chip
                              key={location}
                              label={location}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Notes */}
                      {buyer.notes && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Notes
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {buyer.notes}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Viewings Table */}
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: C21_COLORS.black }}>
                    <HomeIcon sx={{ fontSize: 18, color: C21_COLORS.gold }} />
                    Properties Viewed ({viewings.length})
                  </Typography>

                  {viewings.length > 0 ? (
                    <TableContainer
                      sx={{
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 2,
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Property</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date Seen</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Price</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Compensation</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Offer Submitted</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Comments</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewings.map((viewing) => (
                            <TableRow
                              key={viewing.id}
                              sx={{
                                '&:hover': {
                                  backgroundColor: '#faf7f3',
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                                  {viewing.propertyAddress}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {viewing.dateSeen}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem', color: C21_COLORS.black }}>
                                  ${viewing.price.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={`${viewing.compensation}%`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 22,
                                    backgroundColor: `${C21_COLORS.gold}20`,
                                    color: C21_COLORS.darkGold,
                                    borderColor: C21_COLORS.gold,
                                    fontWeight: 600,
                                  }}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                {viewing.offerSubmitted ? (
                                  <Chip
                                    icon={<SendIcon sx={{ fontSize: 14 }} />}
                                    label="Submitted"
                                    size="small"
                                    color="success"
                                    sx={{
                                      fontSize: '0.7rem',
                                      height: 22,
                                      fontWeight: 500,
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label="Not Submitted"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.7rem',
                                      height: 22,
                                      color: 'text.secondary',
                                      borderColor: 'grey.300',
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', maxWidth: 300 }}>
                                  {viewing.comments}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No property viewings recorded yet.
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {filteredBuyers.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                No buyers found matching your criteria.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BuyerActivityReport;
